import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type ControllerRenderProps } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { type SponsoredAd, type SponsoredAdPackage } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Copy, Loader2, CheckCircle2, LinkIcon, Package, Coins, ArrowRight, Terminal, CreditCard } from "lucide-react";


const validationSchema = z.object({
    gameUrl: z.string().url({ message: "Please enter a valid Roblox game URL." })
});

const creationSchema = z.object({
    packageId: z.string({ required_error: "Please select a sponsorship package." }),
    tokensPerPlayerPerHour: z.coerce.number().int().min(1, "Rate must be at least 1.").max(10000, "Rate cannot exceed 10,000.")
});

// --- Type Definitions --- //

interface GameSponsorFormProps {
    onSuccess: () => void;
}

interface GameDetails {
    universeId: number;
    name: string;
    iconUrl: string;
    creator: { id: number; name: string; type: 'User' | 'Group'; };
}

// --- Helper Functions --- //

const getErrorMessage = (error: any): string => {
    if (!error) return "An unknown error occurred.";
    if (typeof error === 'string') {
        try {
            const parsed = JSON.parse(error);
            return parsed.message || error;
        } catch (e) {
            return error;
        }
    }
    if (error.message) {
        try {
            const parsed = JSON.parse(error.message);
            return parsed.message || error.message;
        } catch (e) {
            return error.message;
        }
    }
    return "An unknown error occurred.";
};

// --- Sub-Components for Form Steps --- //

const PackageSelector = ({ field }: { field: ControllerRenderProps<z.infer<typeof creationSchema>, "packageId"> }) => {
    const { data: packages, isLoading, error } = useQuery<SponsoredAdPackage[], Error>({
        queryKey: ["sponsored-ad-packages"],
        queryFn: () => apiRequest("GET", "/api/sponsored-ads/packages").then(res => res.json()),
    });

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Skeleton className="h-28 w-full" />
                <Skeleton className="h-28 w-full" />
            </div>
        )
    }

    if (error) {
        return (
            <Alert variant="destructive">
                <Terminal className="h-4 w-4" />
                <AlertTitle>Failed to Load Packages</AlertTitle>
                <AlertDescription>
                    {getErrorMessage(error)}
                </AlertDescription>
            </Alert>
        )
    }

    if (!packages || packages.length === 0) {
        return (
            <Alert>
                <Package className="h-4 w-4" />
                <AlertTitle>No Packages Available</AlertTitle>
                <AlertDescription>
                    There are no sponsorship packages available at the moment. Please check back later.
                </AlertDescription>
            </Alert>
        )
    }

    return (
        <RadioGroup onValueChange={field.onChange} defaultValue={field.value} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {packages.map(pkg => (
                <FormItem key={pkg.id}>
                    <FormControl>
                        <RadioGroupItem value={pkg.id} id={`package-${pkg.id}`} className="sr-only" />
                    </FormControl>
                    <Label htmlFor={`package-${pkg.id}`} className="cursor-pointer">
                        <Card className={`hover:border-primary transition-colors ${field.value === pkg.id ? 'border-primary bg-primary/10' : 'bg-muted/50'}`}>
                            <CardContent className="p-4 text-center flex flex-col items-center justify-center h-28">
                                <Package className="h-6 w-6 text-primary mb-2" />
                                <p className="font-semibold text-lg text-foreground">{pkg.name}</p>
                                <p className="text-sm text-primary font-medium">{pkg.tokenAmount.toLocaleString()} Tokens</p>
                            </CardContent>
                        </Card>
                    </Label>
                </FormItem>
            ))}
        </RadioGroup>
    )
};

const CreationSuccess = ({ gameApiKey, onClose }: { gameApiKey: string, onClose: () => void }) => {
    const { toast } = useToast();
    const [showKey, setShowKey] = useState(false);

    const scriptContent = `
local InsertService = game:GetService("InsertService")

local gameKey = "${gameApiKey}" -- Your secret game key
local model = InsertService:LoadAsset(122448807047494)
local SponsorManager = model:FindFirstChild("MainModule", true)

if SponsorManager then
    local module = require(SponsorManager)
    module.Start(gameKey)
end
`;

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        toast({ title: "Copied!", description: `${label} has been copied to your clipboard.` });
    };

    return (
        <div className="flex flex-col max-h-[70vh] sm:max-h-[75vh]">
            <DialogHeader className="text-center flex-shrink-0 pb-4">
                <div className="mx-auto bg-green-100 dark:bg-green-900/50 rounded-full h-12 w-12 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8 text-green-500 dark:text-green-400" />
                </div>
                <DialogTitle className="text-xl sm:text-2xl pt-2">Sponsorship Created!</DialogTitle>
                <DialogDescription>Your game is now ready to attract players.</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 sm:space-y-6 overflow-y-auto flex-grow min-h-0 pr-2 -mr-2">
                <Alert variant="default" className="bg-blue-500/10 border-blue-500/30">
                    <AlertTitle className="text-blue-300 font-semibold text-sm sm:text-base">Action Required: Integrate Your Game</AlertTitle>
                    <AlertDescription className="text-xs sm:text-sm">
                        To start rewarding players, copy the script below and place it inside a `Script` in `ServerScriptService` in your Roblox game.
                    </AlertDescription>
                </Alert>

                <div className="space-y-2">
                    <Label className="text-sm">Server Script</Label>
                    <div className="relative">
                        <div className="bg-muted/50 rounded-md p-3 sm:p-4 overflow-x-auto max-w-full">
                            <pre className="text-[10px] sm:text-xs font-mono whitespace-pre">
                                <code>{scriptContent.trim()}</code>
                            </pre>
                        </div>
                        <Button
                            variant="outline"
                            size="icon"
                            className="absolute top-2 right-2 h-7 w-7 sm:h-8 sm:w-8"
                            onClick={() => copyToClipboard(scriptContent.trim(), "Server Script")}
                        >
                            <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="gameApiKey" className="text-sm">Game API Key (Secret)</Label>
                        <Button variant="link" size="sm" onClick={() => setShowKey(!showKey)} className="text-xs h-auto p-0">
                            {showKey ? 'Hide' : 'Show'}
                        </Button>
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            id="gameApiKey"
                            type={showKey ? 'text' : 'password'}
                            value={gameApiKey}
                            readOnly
                            className="font-mono text-[10px] sm:text-xs flex-1 min-w-0"
                        />
                        <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => copyToClipboard(gameApiKey, "Game API Key")}
                            className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"
                        >
                            <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            <DialogFooter className="flex-shrink-0 pt-4 mt-4 border-t">
                <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">Close</Button>
            </DialogFooter>
        </div>
    );
}

// --- Main Form Component --- //

export function GameSponsorForm({ onSuccess }: GameSponsorFormProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [step, setStep] = useState(1);
    const [validatedGame, setValidatedGame] = useState<GameDetails | null>(null);
    const [createdAd, setCreatedAd] = useState<SponsoredAd | null>(null);

    const validationForm = useForm<z.infer<typeof validationSchema>>({ resolver: zodResolver(validationSchema) });
    const creationForm = useForm<z.infer<typeof creationSchema>>({
        resolver: zodResolver(creationSchema),
        defaultValues: { tokensPerPlayerPerHour: 100 },
    });

    const validateGameMutation = useMutation<GameDetails, Error, { gameUrl: string }>({
        mutationFn: (data) => apiRequest("POST", "/api/sponsored-ads/validate-game", data).then(res => res.json()),
        onSuccess: (data) => {
            toast({ title: "Game Validated!", description: `Successfully verified ownership of "${data.name}".`, variant: 'default' });
            setValidatedGame(data);
            setStep(2);
        },
        onError: (err: any) => {
            toast({ title: "Game Validation Failed", description: getErrorMessage(err), variant: "destructive" });
        }
    });

    const checkoutMutation = useMutation<any, Error, { packageId: string }>({
        mutationFn: (data) => apiRequest("POST", "/api/stripe/create-checkout-session", data).then(res => res.json()),
        onSuccess: (data) => {
            if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
            } else {
                toast({ title: "Error", description: "Could not create a payment session.", variant: "destructive" });
            }
        },
        onError: (err: any) => {
            toast({ title: "Purchase Failed", description: getErrorMessage(err), variant: "destructive" });
        }
    });

    const createAdMutation = useMutation<SponsoredAd, Error, z.infer<typeof creationSchema>>({
        mutationFn: async (data) => {
            const res = await apiRequest("POST", "/api/sponsored-ads", {
                ...data,
                gameUrl: validationForm.getValues("gameUrl"),
            });
            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(errorText || "An unknown error occurred.");
            }
            return res.json();
        },
        onSuccess: (data) => {
            toast({ title: "Purchase Successful!", description: "Your sponsorship has been created." });
            setCreatedAd(data);
            queryClient.invalidateQueries({ queryKey: ['me'] });
            setStep(3);
        },
        onError: (err) => {
            const rawMessage = getErrorMessage(err);
            const message = rawMessage.toLowerCase();

            if (message.includes("insufficient") && message.includes("balance")) {
                const packageId = creationForm.getValues("packageId");
                if (packageId) {
                    toast({
                        title: "Insufficient Balance",
                        description: "Redirecting you to purchase more tokens...",
                    });
                    checkoutMutation.mutate({ packageId });
                } else {
                    toast({ title: "Purchase Failed", description: "Please select a package before purchasing.", variant: "destructive" });
                }
            } else {
                toast({ title: "Purchase Failed", description: rawMessage, variant: "destructive" });
            }
        }
    });

    if (step === 3 && createdAd) {
        return <CreationSuccess gameApiKey={createdAd.gameApiKey} onClose={onSuccess} />;
    }

    return (
        <div className="flex flex-col">
            <DialogHeader className="flex-shrink-0">
                <DialogTitle className="text-xl sm:text-2xl">Create a New Game Sponsorship</DialogTitle>
                <DialogDescription className="text-xs sm:text-sm">
                    {step === 1 ? "Verify ownership of your Roblox game to begin." : "Select a package and set your reward rate."}
                </DialogDescription>
            </DialogHeader>

            {step === 1 && (
                <Form {...validationForm}>
                    <form onSubmit={validationForm.handleSubmit((values) => validateGameMutation.mutate(values))} className="py-4 space-y-4 sm:space-y-6">
                        <FormField
                            control={validationForm.control}
                            name="gameUrl"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-sm sm:text-base">Roblox Game URL</FormLabel>
                                    <div className="relative">
                                        <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <FormControl><Input placeholder="https://www.roblox.com/games/12345/..." {...field} className="pl-10 text-sm" /></FormControl>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <DialogFooter className="gap-2">
                            <Button type="button" variant="ghost" onClick={onSuccess} className="flex-1 sm:flex-initial">Cancel</Button>
                            <Button type="submit" disabled={validateGameMutation.isPending} className="flex-1 sm:flex-initial">
                                {validateGameMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} 
                                Validate Game 
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            )}

            {step === 2 && validatedGame && (
                <Form {...creationForm}>
                    <form onSubmit={creationForm.handleSubmit((values) => createAdMutation.mutate(values))} className="flex flex-col max-h-[70vh] sm:max-h-[75vh]">
                        <div className="space-y-4 sm:space-y-6 overflow-y-auto flex-grow min-h-0 pr-2 -mr-2">
                            <Card className="bg-muted/50 p-3 sm:p-4 flex items-center">
                                <img src={validatedGame.iconUrl} alt={validatedGame.name} className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg object-cover flex-shrink-0" />
                                <div className="ml-3 sm:ml-4 min-w-0 flex-1">
                                    <p className="font-semibold text-foreground text-sm sm:text-base truncate">{validatedGame.name}</p>
                                    <p className="text-xs sm:text-sm text-muted-foreground truncate">Created by {validatedGame.creator.name}</p>
                                </div>
                            </Card>

                            <FormField
                                control={creationForm.control}
                                name="packageId"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm sm:text-base">Sponsorship Package</FormLabel>
                                        <FormControl>
                                            <PackageSelector field={field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={creationForm.control}
                                name="tokensPerPlayerPerHour"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm sm:text-base">Tokens per Player, per Hour</FormLabel>
                                        <div className="relative">
                                            <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                            <FormControl><Input type="number" {...field} className="pl-10" /></FormControl>
                                        </div>
                                        <FormDescription className="text-xs sm:text-sm">The amount of tokens a player earns for each hour they play your game.</FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <DialogFooter className="flex-shrink-0 pt-4 mt-4 border-t gap-2">
                            <Button type="button" variant="ghost" onClick={() => setStep(1)} className="flex-1 sm:flex-initial">Back</Button>
                            <Button
                                type="submit"
                                disabled={createAdMutation.isPending || checkoutMutation.isPending}
                                className="flex-1 sm:flex-initial"
                            >
                                {(createAdMutation.isPending || checkoutMutation.isPending) ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {checkoutMutation.isPending ? 'Redirecting...' : 'Processing...'}
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="mr-2 h-4 w-4" /> Purchase & Create
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            )}
        </div>
    );
}